import type { MCQQuestion } from "@/lib/powtoon-be/types";

/** Default # of questions to request from the LLM during generation. */
export const DEFAULT_GENERATE_MCQ_COUNT = 5;
/** Default overall pass rate as a percentage (1..100). */
export const DEFAULT_PASS_RATE = 80;

function randomId(prefix: string) {
  const rand = Math.random().toString(36).slice(2, 10);
  return `${prefix}_${Date.now().toString(36)}_${rand}`;
}

function newId() {
  return randomId("q");
}

export function newQuestionnaireId() {
  return randomId("qz");
}

export function emptyQuestion(): MCQQuestion {
  return {
    id: newId(),
    question: "",
    amount_of_correct_answers_to_pass: 1,
    answers: [{ text: "" }],
  };
}

/**
 * Plain-text serialization used inside the generated context prompt the
 * tutor receives. Lists each question with its valid answer items and the
 * minimum number the learner must produce.
 */
export function serializeQuestions(questions: MCQQuestion[]): string {
  if (!questions.length) return "";
  return questions
    .map((q, qi) => {
      const header = `${qi + 1}. ${q.question.trim()}`;
      const validAnswers = q.answers
        .map((a) => a.text.trim())
        .filter((t) => t.length > 0);
      const answerLine = `   Valid answers: ${validAnswers.join(", ")}`;
      const min = Math.max(
        1,
        Math.min(q.amount_of_correct_answers_to_pass || 1, Math.max(validAnswers.length, 1)),
      );
      const minLine = `   Minimum correct to pass: ${min}`;
      return [header, answerLine, minLine].join("\n");
    })
    .join("\n\n");
}

/** Coerce server/legacy questions into the current shape. */
export function normalizeIncoming(
  questions: MCQQuestion[] | undefined,
): MCQQuestion[] {
  if (!questions) return [];
  return questions.map((q) => {
    const answers = Array.isArray(q?.answers)
      ? q.answers
          .slice(0, 8)
          .map((a) => ({ text: typeof a?.text === "string" ? a.text : "" }))
      : [{ text: "" }];
    const rawMin = (q as unknown as { amount_of_correct_answers_to_pass?: unknown })
      ?.amount_of_correct_answers_to_pass;
    const parsedMin = typeof rawMin === "number" ? rawMin : Number(rawMin);
    const safeMin = Number.isFinite(parsedMin) && parsedMin > 0 ? Math.floor(parsedMin) : 1;
    const clampedMin = Math.max(1, Math.min(safeMin, Math.max(answers.length, 1)));
    return {
      id: q?.id || newId(),
      question: q?.question ?? "",
      amount_of_correct_answers_to_pass: clampedMin,
      answers: answers.length > 0 ? answers : [{ text: "" }],
    };
  });
}
